import { memo, useRef } from "react"
import { useNavigation } from "react-router-dom-7"

import { Href, HSpace, IconButton, Loading, MaterialIcon } from "~/shared/ui"

import cssModule from "./index.module.scss"
import { Menu, MenuItem, useMenu } from "~/shared/ui/kit"
import { createPortal } from "react-dom"
import { cx } from "classix"
import { useMediaQuery } from "react-responsive"
import { breakPoints } from "~/shared/ui/constants"

export type BreadcrumbType = {
	title: string
	url?: string
}

export type BreadcrumbLinksType = Array<BreadcrumbType>

function compareLinks(
	a: BreadcrumbLinksType | undefined,
	b: BreadcrumbLinksType | undefined
) {
	if (a === b) return true

	//if (a === undefined && b !== undefined || a !== undefined && b === undefined) return false
	if (a === undefined || b === undefined) return false

	if (a.length !== b.length) return false

	for (let i = 0; i < a.length; ++i) {
		const ai = a[i]
		const bi = b[i]

		if (ai.url !== bi.url || ai.title != bi.title) return false
	}

	return true
}

function PageLoading() {
	const navigation = useNavigation()
	return navigation.state === "loading" ? <><HSpace size={0.5} /><Loading loading spinnerSize="1rem"
																			title="" /></> : null
}

function Divider() {
	return <MaterialIcon name="navigate_next" style={{ fontSize: "1.5rem", marginTop: "0.1rem" }} />
}

function BreadcrumbLinks({ links }: { links: BreadcrumbLinksType }) {

	return (
		<div className="flex gap-[0.25rem] items-center flex-wrap">
			{links.map(({ title, url }: { title: string, url?: string }, index) => {
				const isLastLink = index === links.length - 1

				return isLastLink
					? <div key={title} className={cssModule.breadcrumb}>
						{index !== 0 && <Divider />}
						<span
							className={cssModule.lastBreadcrumbLink}>{title}</span>
					</div>
					: <div key={title} className={cssModule.breadcrumb}>
						{index !== 0 && <Divider />}
						{url ? <Href href={url as string}>{title}</Href> : <span>{title}</span>}
					</div>
			})}
		</div>
	)
}

function CompactBreadcrumbLinks({
									firstLink, links, lastLink
								}: {
	firstLink: BreadcrumbType,
	links: BreadcrumbType[],
	lastLink: BreadcrumbType
}) {
	const menuAnchorRef = useRef<HTMLButtonElement>(null)
	const [menu, showMenu] = useMenu(menuAnchorRef, { x: 0.5, y: 1 })

	return (
		<>
			<div className="flex gap-[0.25rem] items-center flex-wrap">
				<div className={cssModule.breadcrumb}>
					{firstLink.url
						? <Href href={firstLink.url as string}>{firstLink.title}</Href>
						: <span>{firstLink.title}</span>}
				</div>
				<div className={cssModule.breadcrumb}>
					<Divider />
					<IconButton ref={menuAnchorRef} iconSize="1rem" style={{ width: "1.5rem", height: "1.5rem" }}
								transparent
								colorStyle="dark" icon="more_vert"
								onClick={showMenu}
					/>
				</div>
				<div className={cssModule.breadcrumb}>
					<Divider />
					<span className={cssModule.lastBreadcrumbLink}>{lastLink.title}</span>
				</div>
			</div>
			{createPortal(
				<Menu {...menu} style={{
					left: "2rem",
					marginTop: "0.25rem",
					width: "calc(100% - 4rem)",
					borderRadius: "0.5rem",
					borderColor: "#9fa9b5"
				}} className={cx(cssModule.breadcrumbs, cssModule.menu)}>
					{links.map(link => <MenuItem key={link.title}>
						<div className={cssModule.breadcrumb}>
							<Divider />
							{link.url
								? <Href href={link.url as string}>{link.title}</Href>
								: <span>{link.title}</span>}
						</div>
					</MenuItem>)}

				</Menu>,
				document.body
			)}
		</>
	)
}

function AdaptiveBreadcrumbLinks({ links }: { links: BreadcrumbLinksType }) {

	const isSmallDevice = useMediaQuery({ maxWidth: breakPoints.sm })

	if (!isSmallDevice || links.length < 4) return <BreadcrumbLinks links={links} />

	const firstLink = links[0]
	const slicedLinks = links.slice(1, -1)
	const lastLink = links[links.length - 1]

	return <CompactBreadcrumbLinks firstLink={firstLink} links={slicedLinks} lastLink={lastLink} />
}

export const Breadcrumbs = memo(
	function Breadcrumbs({
							 links,
							 backUrl
						 }: {
		links: BreadcrumbLinksType
		backUrl: string
	}) {
		let targetUrl = backUrl

		if (targetUrl) {
			for (let i = links.length - 2; i >= 0; --i) {
				const link = links[i]
				if (link.url) {
					targetUrl = link.url
					break
				}
			}
		}

		return (
			<nav className={cssModule.breadcrumbs}>
				<IconButton
					elementType={Href}
					href={targetUrl}
					className={cssModule.topbarBackButton}
					icon="arrow_back"
					transparent
					borderless
					colorStyle="dark"
				/>
				<AdaptiveBreadcrumbLinks links={links} />
				<PageLoading />
			</nav>
		)
	},
	(prev, next) => compareLinks(next.links, prev.links)
)
